import { useEffect, useState, useCallback } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { getDashboardStats, getLeaderboardData, getCalls, getHomeworkStats } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Phone, 
  Users, 
  AlertTriangle, 
  TrendingUp, 
  Star,
  CheckCircle,
  AlertCircle,
  Trophy,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Brain,
  BookOpen
} from 'lucide-react';
import { 
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis
} from 'recharts';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'];

const FILTER_PERIODS = [
  { value: 'daily', label: 'Today' },
  { value: 'weekly', label: 'This Week' },
  { value: 'monthly', label: 'This Month' },
  { value: 'all', label: 'All Time' },
];

export default function AdminDashboard() {
  const outletContext = useOutletContext() || {};
  const { adminUrl } = outletContext;
  const navigate = useNavigate();
  
  const buildLink = (path) => adminUrl ? `/admin/${adminUrl}/${path}` : `/dashboard/${path}`;
  
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [homeworkStats, setHomeworkStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('daily');
  const [leaderboardExpanded, setLeaderboardExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsRes, leaderboardRes, callsRes, homeworkRes] = await Promise.all([
        getDashboardStats().catch(() => ({ data: {} })),
        getLeaderboardData(globalFilter).catch(() => ({ data: [] })),
        getCalls({ limit: 5 }).catch(() => ({ data: { calls: [] } })),
        getHomeworkStats().catch(() => ({ data: {} }))
      ]);
      
      setStats(statsRes.data);
      setLeaderboard(Array.isArray(leaderboardRes.data) ? leaderboardRes.data : leaderboardRes.data?.leaderboard || []);
      setRecentCalls(callsRes.data?.calls || []);
      setHomeworkStats(homeworkRes.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [globalFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Stat cards configuration
  const statCards = [
    {
      title: 'Calls Analyzed',
      value: stats?.total_calls || 0,
      icon: Phone,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      subtitle: `${stats?.processed_calls || 0} processed`,
      subtitleIcon: CheckCircle,
      subtitleIconColor: 'text-emerald-500',
      link: buildLink('calls'),
      testId: 'total-calls-card'
    },
    {
      title: 'Team Members',
      value: stats?.total_users || 0,
      icon: Users,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      subtitle: `${stats?.total_departments || 0} departments`,
      subtitleIcon: CheckCircle,
      subtitleIconColor: 'text-purple-500',
      link: buildLink('users'),
      testId: 'total-users-card'
    },
    {
      title: 'Avg. Score',
      value: stats?.average_score || '0.0',
      icon: Star,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      subtitle: 'Team average',
      subtitleIcon: null,
      link: buildLink('leaderboard'),
      testId: 'avg-score-card'
    },
    {
      title: 'Active Alerts',
      value: (stats?.critical_alerts || 0) + (stats?.medium_alerts || 0),
      icon: AlertTriangle,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      valueColor: 'text-red-600',
      subtitle: `${stats?.critical_alerts || 0} critical`,
      subtitleIcon: AlertCircle,
      subtitleIconColor: 'text-red-500',
      link: buildLink('reports'),
      testId: 'alerts-card'
    },
  ];

  // Classification data for pie chart
  const classificationData = [
    { name: 'Best', value: stats?.best_calls || 0, color: '#10B981' },
    { name: 'Good', value: stats?.good_calls || 0, color: '#3B82F6' },
    { name: 'Improvement', value: stats?.improvement_calls || 0, color: '#F59E0B' },
    { name: 'High Alert', value: stats?.high_alert_calls || 0, color: '#EF4444' },
  ];

  // AI Analysis data
  const aiAnalysisData = {
    total_analyzed: stats?.processed_calls || 0,
    improvement_trend: '+5.2%',
    common_issues: ['Opening rapport', 'Closing techniques', 'Product knowledge']
  };

  // Homework progress data
  const homeworkProgressData = [
    { name: 'Completed', value: homeworkStats?.completed || 12, fill: '#10B981' },
    { name: 'In Progress', value: homeworkStats?.in_progress || 5, fill: '#3B82F6' },
    { name: 'Pending', value: homeworkStats?.pending || 8, fill: '#F59E0B' },
  ];

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-600';
    if (score >= 6) return 'text-blue-600';
    if (score >= 4) return 'text-amber-600';
    return 'text-red-600';
  };

  const getClassificationBadge = (classification) => {
    const variants = {
      best: 'bg-emerald-100 text-emerald-700',
      good: 'bg-blue-100 text-blue-700',
      improvement: 'bg-amber-100 text-amber-700',
      high_alert: 'bg-red-100 text-red-700'
    };
    return variants[classification] || 'bg-muted text-slate-700';
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="card-elevated">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your call quality metrics</p>
        </div>
        <Select value={globalFilter} onValueChange={setGlobalFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {FILTER_PERIODS.map((period) => (
              <SelectItem key={period.value} value={period.value}>
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, idx) => (
          <Link key={card.title} to={card.link} data-testid={card.testId}>
            <Card className={`card-elevated cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium">{card.title}</p>
                    <p className={`text-3xl font-bold mt-1 ${card.valueColor || 'text-foreground'}`}>
                      {card.value}
                    </p>
                  </div>
                  <div className={`p-2 ${card.iconBg} rounded-lg group-hover:scale-110 transition-transform`}>
                    <card.icon className={`h-5 w-5 ${card.iconColor}`} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    {card.subtitleIcon && (
                      <card.subtitleIcon className={`h-4 w-4 ${card.subtitleIconColor}`} />
                    )}
                    <span className="text-muted-foreground">{card.subtitle}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Classifications Chart */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-lg">Call Classifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={classificationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {classificationData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {classificationData.map((item) => (
                <Link 
                  key={item.name} 
                  to={`${buildLink('calls')}?classification=${item.name.toLowerCase().replace(' ', '_')}`}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Leaderboard
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono text-xs">
                {FILTER_PERIODS.find(p => p.value === globalFilter)?.label}
              </Badge>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setLeaderboardExpanded(!leaderboardExpanded)}
              >
                {leaderboardExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {leaderboard.length > 0 ? leaderboard.slice(0, leaderboardExpanded ? 10 : 5).map((leader, idx) => (
                <Link 
                  key={leader.user_id}
                  to={buildLink('users')}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                    ${idx === 0 ? 'bg-amber-500/20 text-amber-500' : 
                      idx === 1 ? 'bg-muted text-muted-foreground' : 
                      idx === 2 ? 'bg-orange-500/20 text-orange-500' : 
                      'bg-muted text-muted-foreground'}`}
                  >
                    {leader.rank || idx + 1}
                  </div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={leader.avatar_url} alt={leader.user_name} />
                    <AvatarFallback className="bg-muted">
                      {leader.user_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate group-hover:text-blue-500 transition-colors">
                      {leader.user_name}
                    </p>
                    <p className="text-sm text-muted-foreground">{leader.call_count || leader.total_calls} calls</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-foreground">{leader.average_score || leader.avg_score}</p>
                    <p className="text-xs text-muted-foreground">avg score</p>
                  </div>
                </Link>
              )) : (
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No leaderboard data for this period</p>
                </div>
              )}
            </div>
            {leaderboard.length > 5 && (
              <div className="mt-4 pt-4 border-t border-border">
                <Button 
                  variant="ghost" 
                  className="w-full text-blue-600 hover:text-blue-700"
                  onClick={() => setLeaderboardExpanded(!leaderboardExpanded)}
                >
                  {leaderboardExpanded ? 'Collapse' : 'View Full Leaderboard'}
                  {leaderboardExpanded ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Analysis Overview */}
        <Card className="card-elevated bg-gradient-to-br from-purple-50 to-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              AI Analysis Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground">Calls Analyzed</p>
                  <p className="text-2xl font-bold text-purple-500">{aiAnalysisData.total_analyzed}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg border border-border">
                  <p className="text-sm text-muted-foreground">Improvement Trend</p>
                  <p className="text-2xl font-bold text-emerald-500">{aiAnalysisData.improvement_trend}</p>
                </div>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-2">Common Areas for Improvement</p>
                <div className="space-y-2">
                  {aiAnalysisData.common_issues.map((issue, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="text-sm text-foreground">{issue}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <Link 
                to={buildLink('coaching')}
                className="flex items-center justify-center gap-2 text-sm text-purple-500 hover:text-purple-400 font-medium"
              >
                View AI Coaching Insights
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Homework Progress */}
        <Card className="card-elevated bg-gradient-to-br from-emerald-500/10 to-background">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-emerald-500" />
              Homework AI Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={homeworkProgressData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {homeworkProgressData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Tasks</span>
                <span className="font-medium text-foreground">{homeworkProgressData.reduce((a, b) => a + b.value, 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Completion Rate</span>
                <span className="font-medium text-emerald-500">
                  {homeworkProgressData.reduce((a, b) => a + b.value, 0) > 0 
                    ? Math.round((homeworkProgressData[0].value / homeworkProgressData.reduce((a, b) => a + b.value, 0)) * 100)
                    : 0}%
                </span>
              </div>
            </div>
            
            <Link 
              to={buildLink('homework')}
              className="flex items-center justify-center gap-2 text-sm text-emerald-500 hover:text-emerald-400 font-medium mt-4"
            >
              View Homework Dashboard
              <ChevronRight className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>

        {/* Recent Calls */}
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Calls</CardTitle>
            <Link 
              to={buildLink('calls')}
              className="text-sm text-blue-500 hover:text-blue-400 flex items-center gap-1"
            >
              View All
              <ChevronRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentCalls.length > 0 ? recentCalls.slice(0, 5).map((call) => (
                <Link 
                  key={call.id}
                  to={`${buildLink('calls')}/${call.id}`}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate group-hover:text-blue-500 transition-colors">
                      {call.user_name || 'Unknown User'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(call.call_date || call.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${getScoreColor(call.average_score)}`}>
                      {call.average_score?.toFixed(1) || '-'}
                    </p>
                    <Badge className={getClassificationBadge(call.classification)}>
                      {call.classification?.replace('_', ' ') || 'N/A'}
                    </Badge>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                </Link>
              )) : (
                <div className="text-center py-8">
                  <Phone className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No recent calls</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
